import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';

// Types
import { Subscription } from 'Shared/constants/types';
import { BillingPlan } from 'Shared/types/Organization.type';

// Constants
import { billingStates } from 'Shared/constants/constants';

// Helpers
import { numberToCurrency } from 'Shared/helpers';

// Components
import WpIcon from 'Wpds/components/icon/WpIcon';
import WpLabelled from 'Wpds/components/labelled/WpLabelled';
import { WpBadge } from 'Wpds/components/badge';
import WpAlert from 'Wpds/components/alert/WpAlert';

// Styles
import './PlanSubscriptionDetails.scss';

type Props = {
  subscription?: Subscription;
};

const PlanSubscriptionDetails = ({ subscription }: Props) => {
  const { seatsToBillFor, billingState, daysLeftInTrial } = useSelector(state => state.organization);

  const [planName, setPlanName] = useState(null);

  const hasSubscription = Boolean(subscription);
  const isTrialExpired = billingState === billingStates.TRIAL_EXPIRED;
  const isTrial = billingState === billingStates.FREE_TRIAL;
  const isBasic = billingState === billingStates.FREE_PLAN;
  const isStartup = hasSubscription && subscription.plan_name === 'Startup Plan';
  const isGrowth = hasSubscription && subscription.plan_name === 'Growth Plan';

  const daysLeftInTrialToRender = daysLeftInTrial < 0 ? 0 : daysLeftInTrial;

  const isGoalsEnabled = hasSubscription && subscription.features.goals.state === BillingPlan.paidPlan;
  const isReviewsEnabled = hasSubscription && subscription.features.reviews.state === BillingPlan.paidPlan;

  useEffect(() => {
    if (billingState === billingStates.FREE_TRIAL || billingState === billingStates.TRIAL_EXPIRED) {
      setPlanName('Free Trial of Growth Plan');
    } else if (billingState === billingStates.FREE_PLAN) {
      setPlanName('Basic Plan');
    } else setPlanName(subscription?.plan_name);
  }, [subscription]);

  const baseClassName = 'PlanSubscriptionDetails';

  const Attribute = ({ label, children }: { label: string; children: string | React.ReactNode }) => {
    return (
      <div className={`${baseClassName}__attributes__attr`}>
        <WpLabelled id={`${label.replace(/\s/g, '')}-label`} label={label} showRequiredIndicator={false}>
          {children}
        </WpLabelled>
      </div>
    );
  };

  const SubscriptionAddOns = () => {
    return (
      <Attribute label="Add-ons">
        {isGoalsEnabled || isReviewsEnabled ? (
          <ul className={`${baseClassName}__addons-list`}>
            {isGoalsEnabled && <li>Goals</li>}
            {isReviewsEnabled && <li>Reviews</li>}
          </ul>
        ) : (
          <em>no add-ons included</em>
        )}
      </Attribute>
    );
  };

  return (
    <div className={baseClassName}>
      <div className={`${baseClassName}__name-wrapper`}>
        <div className={`${baseClassName}__name-wrapper__icon`}>
          <WpIcon name="cube" />
        </div>
        <p className={`${baseClassName}__name-wrapper__name`}>{planName}</p>
        {isTrialExpired && <WpBadge color="red">Trial Expired</WpBadge>}
      </div>
      {(isTrial || isTrialExpired) && (
        <div className={`${baseClassName}__note`}>
          You have&nbsp;
          <b>
            {daysLeftInTrialToRender} day{daysLeftInTrialToRender === 1 ? '' : 's'}
          </b>
          &nbsp;remaining in your free trial. Your free trial includes access to all our premium add-ons.
        </div>
      )}
      {(isTrial || isTrialExpired) && (
        <div className={`${baseClassName}__attributes`}>
          <Attribute label="Add-ons">
            <ul className={`${baseClassName}__addons-list`}>
              <li>
                Goals
                <span className={`${baseClassName}__addons-list__badge`}>
                  <WpBadge size="sm">Trial</WpBadge>
                </span>
              </li>
              <li>
                Reviews
                <span className={`${baseClassName}__addons-list__badge`}>
                  <WpBadge size="sm">Trial</WpBadge>
                </span>
              </li>
            </ul>
          </Attribute>
          <Attribute label="Price">$0</Attribute>
        </div>
      )}
      {isBasic && (
        <div className={`${baseClassName}__attributes`}>
          <Attribute label="Add-ons">
            <em>no add-ons included</em>
          </Attribute>
          <Attribute label="User Count">{seatsToBillFor} / 4</Attribute>
          <Attribute label="Price per user / mo">$0</Attribute>
        </div>
      )}
      {isStartup && (
        <div className={`${baseClassName}__attributes`}>
          <SubscriptionAddOns />
          <Attribute label="Price per user / mo"> {numberToCurrency(subscription.price_per_month / 100)}</Attribute>
          <Attribute label="User count">{seatsToBillFor}</Attribute>
          <Attribute label={`Renews on (${subscription.billing_cycle})`}>
            {moment.unix(subscription.next_billing_date).format('MMMM Do, YYYY')}
          </Attribute>
          <Attribute label="Renewal amount">{numberToCurrency(subscription.upcoming_invoice.amount / 100)}</Attribute>
        </div>
      )}
      {isGrowth && (
        <div className={`${baseClassName}__attributes`}>
          <SubscriptionAddOns />
          <Attribute label="User count">{seatsToBillFor}</Attribute>
          <Attribute label="Next bill date">
            {moment.unix(subscription.next_billing_date).format('MMMM Do, YYYY')}
          </Attribute>
          <Attribute label="Next bill amt">
            {numberToCurrency(subscription.upcoming_invoice.subscription_amount / 100)}
          </Attribute>
        </div>
      )}

      {hasSubscription && subscription.billing_cycle === 'monthly' && subscription.proration.amount !== 0 && (
        <WpAlert>
          Adjusted for prorations, you will be billed{' '}
          <strong>{numberToCurrency(Math.abs(subscription.upcoming_invoice.amount) / 100)}</strong> on{' '}
          <strong>{moment.unix(subscription.next_billing_date).format('MMMM Do, YYYY')}</strong>.
        </WpAlert>
      )}
      {hasSubscription && subscription.billing_cycle === 'annually' && subscription.proration.amount > 0 && (
        <WpAlert>
          Adjusted for prorations, you will be billed{' '}
          <strong>{numberToCurrency(Math.abs(subscription.proration.amount) / 100)}</strong> on{' '}
          <strong>{moment.unix(subscription.proration.due_on).format('MMMM Do, YYYY')}</strong>.
        </WpAlert>
      )}
      {hasSubscription && subscription.billing_cycle === 'annually' && subscription.proration.amount < 0 && (
        <WpAlert>
          Adjusted for prorations, you will be credited{' '}
          <strong>{numberToCurrency(Math.abs(subscription.proration.amount) / 100)}</strong> on{' '}
          <strong>{moment.unix(subscription.next_billing_date).format('MMMM Do, YYYY')}</strong>.
        </WpAlert>
      )}
      <p className={`${baseClassName}__note`}>
        {/* TODO: PER-260 */}
        <a href="https://www.predictiveindex.com/plans/" target="_blank" rel="noreferrer">
          Learn more about plans and pricing
        </a>
      </p>
    </div>
  );
};

export default PlanSubscriptionDetails;
